import { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  ArrowLeft,
  ArrowRight,
  Camera01,
  Loading01 as Loader,
  Stars02,
} from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { QE_PHOTO_COPY } from "./copy"
import type { QEPhoto } from "./types"
import type { QuickEstimateActions } from "./use-quick-estimate-state"
import type { UseQECapture } from "./use-qe-capture"
import { QualityEditor } from "./quality-editor"
import { QualityTile } from "./quality-tile"
import { EmptyStateIllustration } from "./empty-state"

// Quick Estimate 3 — Step 2 (photo capture + quality verdict).
//
// This step is purely "did the shot come out usable?". The AI also
// returns a side label, but we deliberately don't surface it here —
// users review and correct sides on the next step (Tags) where they
// have undivided attention for that one question.
//
// Tap the hero shutter or the "+ Add" tile to open the camera.
// Tap any photo to open the quality editor (verdict + retake / retry
// / keep / remove + optional note).

interface StepPhotosProps {
  photos: QEPhoto[]
  actions: QuickEstimateActions
  capture: UseQECapture
  onBack: () => void
  onContinue: () => void
  /** Overrides eyebrow when this step is reused inside a wizard with
   *  a different total step count (e.g. full-estimate's 6 steps). */
  eyebrowLabel?: string
}

export function StepPhotos({
  photos,
  actions,
  capture,
  onBack,
  onContinue,
  eyebrowLabel,
}: StepPhotosProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const editingPhoto = useMemo(
    () => photos.find((p) => p.id === editingId) ?? null,
    [photos, editingId],
  )

  useEffect(() => {
    if (editingId && !editingPhoto) setEditingId(null)
  }, [editingId, editingPhoto])

  const handleSendWithoutTag = (photo: QEPhoto) => {
    actions.updatePhoto(photo.id, {
      sendWithoutAiTag: true,
      verdict: "good",
    })
    setEditingId(null)
  }

  const handleKeepAnyway = (photo: QEPhoto) => {
    // Mark the photo as quality-passed despite the AI flagging it.
    // We don't lose the original score/issues — the editor still
    // reads them — but the tile reads as "kept" instead of "retake".
    actions.updatePhoto(photo.id, {
      qualified: true,
    })
    setEditingId(null)
  }

  const handleRetake = (photo: QEPhoto) => {
    capture.forgetFile(photo.id)
    actions.removePhoto(photo.id)
    setEditingId(null)
    setTimeout(() => capture.triggerCapture(), 50)
  }

  const handleRemove = (photo: QEPhoto) => {
    capture.forgetFile(photo.id)
    actions.removePhoto(photo.id)
    setEditingId((curr) => (curr === photo.id ? null : curr))
  }

  const handleChangeDescription = (photo: QEPhoto, text: string) => {
    actions.updatePhoto(photo.id, { description: text })
  }

  // Live AI activity chip — drives the indigo summary chip below the
  // shutter. Reads "AI reviewing N…" while in-flight, otherwise a
  // breakdown of clean vs needs-attention.
  const inflightCount = capture.inflight.length
  const cleanCount = photos.filter(
    (p) =>
      p.uploadedUrl.length > 0 &&
      (p.qualified || p.sendWithoutAiTag) &&
      p.verdict !== "eval-failed",
  ).length
  const issuesCount = photos.filter(
    (p) =>
      p.verdict === "eval-failed" ||
      (p.uploadedUrl.length > 0 && !p.qualified && !p.sendWithoutAiTag),
  ).length

  const activityText =
    inflightCount > 0
      ? QE_PHOTO_COPY.aiReviewing(inflightCount)
      : photos.length === 0
        ? QE_PHOTO_COPY.aiReady
        : issuesCount === 0
          ? QE_PHOTO_COPY.aiAllClean(cleanCount)
          : QE_PHOTO_COPY.aiSomeIssues(cleanCount, issuesCount)

  const totalCount = photos.length + inflightCount
  const submittableCount = photos.filter(
    (p) => p.uploadedUrl.length > 0 || p.sendWithoutAiTag,
  ).length
  const canContinue = submittableCount > 0 && inflightCount === 0

  return (
    <div className="space-y-6 pb-36 sm:pb-0">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          {eyebrowLabel ?? QE_PHOTO_COPY.eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {QE_PHOTO_COPY.title}
        </h2>
        <p className="mt-1.5 text-[14px] text-zinc-500 leading-relaxed">
          {QE_PHOTO_COPY.subtitle}
        </p>
      </header>

      {/* Add-photo affordance — exactly one. Hero shutter dominates
          the empty state; once photos exist we drop to a compact
          full-width button so the grid gets the vertical space it
          needs. The previous "hero + AddTile" pair felt duplicated. */}
      {totalCount === 0 ? (
        <ShutterButton onClick={capture.triggerCapture} attentionMode />
      ) : (
        <CompactCaptureButton onClick={capture.triggerCapture} />
      )}

      <ActivityChip text={activityText} hasInflight={inflightCount > 0} />

      {totalCount === 0 ? (
        <EmptyStateIllustration />
      ) : (
        <div className="space-y-3">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            {totalCount === 1
              ? QE_PHOTO_COPY.countOne
              : QE_PHOTO_COPY.countMany(totalCount)}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <AnimatePresence initial={false} mode="popLayout">
              {photos.map((photo) => (
                <motion.div
                  key={`shot-${photo.id}`}
                  layout
                  initial={{ opacity: 0, scale: 0.88, filter: "blur(6px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 0.86, filter: "blur(4px)" }}
                  transition={{
                    type: "spring",
                    stiffness: 320,
                    damping: 26,
                    filter: { duration: 0.32, ease: "easeOut" },
                  }}
                >
                  <QualityTile
                    photo={photo}
                    onOpen={() => setEditingId(photo.id)}
                  />
                </motion.div>
              ))}
              {capture.inflight.map((p) => (
                <motion.div
                  key={`inflight-${p.id}`}
                  layout
                  initial={{ opacity: 0, scale: 0.86 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.86 }}
                  transition={{ type: "spring", stiffness: 320, damping: 26 }}
                >
                  <InflightTile previewUrl={p.previewUrl} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* CTA bar — fixed-bottom on mobile, static on desktop. */}
      <div
        className={cx(
          "space-y-2",
          "fixed inset-x-0 bottom-0 z-30",
          "px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)]",
          "bg-gradient-to-t from-white via-white/95 to-white/0",
          "sm:static sm:px-0 sm:pt-2 sm:pb-0 sm:bg-transparent",
        )}
      >
        {!canContinue && submittableCount === 0 && totalCount > 0 && (
          <p className="text-center text-[12.5px] font-medium text-amber-700">
            Keep, retake, or use-without-tag at least 1 photo to continue.
          </p>
        )}
        {inflightCount > 0 && submittableCount > 0 && (
          <p className="text-center text-[12.5px] font-medium text-zinc-500">
            Hang on — AI is still reviewing {inflightCount}.
          </p>
        )}
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className={cx(
            "w-full h-14 rounded-2xl text-[15px] font-semibold flex items-center justify-center gap-2 transition-all duration-150",
            canContinue
              ? "bg-zinc-900 hover:bg-black text-white shadow-lg shadow-zinc-900/15 hover:shadow-zinc-900/25"
              : "bg-zinc-100 text-zinc-400 cursor-not-allowed",
          )}
        >
          {canContinue
            ? QE_PHOTO_COPY.continue
            : QE_PHOTO_COPY.continueDisabled}
          {canContinue && <ArrowRight className="size-4" aria-hidden />}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="w-full h-10 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {QE_PHOTO_COPY.back}
        </button>
      </div>

      <QualityEditor
        open={!!editingPhoto}
        photo={editingPhoto}
        canRetry={
          !!editingPhoto &&
          capture.hasFileRef(editingPhoto.id) &&
          (editingPhoto.retryCount ?? 0) < 3
        }
        retrying={capture.retryingId === editingPhoto?.id}
        onClose={() => setEditingId(null)}
        onChangeDescription={(text) =>
          editingPhoto && handleChangeDescription(editingPhoto, text)
        }
        onRetake={
          editingPhoto && capture.hasFileRef(editingPhoto.id)
            ? () => handleRetake(editingPhoto)
            : undefined
        }
        onRetry={
          editingPhoto && capture.hasFileRef(editingPhoto.id)
            ? () => capture.handleRetry(editingPhoto)
            : undefined
        }
        onKeepAnyway={
          editingPhoto &&
          editingPhoto.uploadedUrl.length > 0 &&
          !editingPhoto.qualified &&
          editingPhoto.verdict !== "eval-failed"
            ? () => handleKeepAnyway(editingPhoto)
            : undefined
        }
        onSendWithoutTag={
          editingPhoto && editingPhoto.verdict === "eval-failed"
            ? () => handleSendWithoutTag(editingPhoto)
            : undefined
        }
        onRemove={() => editingPhoto && handleRemove(editingPhoto)}
      />
    </div>
  )
}

/* ── Hero shutter ─────────────────────────────────────────────── */

function ShutterButton({
  onClick,
  attentionMode,
}: {
  onClick: () => void
  attentionMode: boolean
}) {
  return (
    <div className="relative flex flex-col items-center pt-1">
      <AnimatePresence>
        {attentionMode && (
          <motion.div
            key="halo"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4 }}
            className="absolute -inset-5 -z-0 pointer-events-none"
          >
            <motion.div
              className="absolute inset-0 rounded-full bg-emerald-400/25 blur-2xl"
              animate={{
                opacity: [0.45, 0.75, 0.45],
                scale: [0.95, 1.06, 0.95],
              }}
              transition={{
                duration: 2.6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button
        type="button"
        onClick={onClick}
        whileTap={{ scale: 0.93, rotate: -1 }}
        className={cx(
          "relative z-10",
          "size-24 md:size-28 rounded-full",
          "ring-[6px] ring-white",
          "outline outline-1 outline-emerald-700/15",
          "shadow-[0_22px_50px_-18px_rgba(5,150,105,0.55)] hover:shadow-[0_30px_60px_-18px_rgba(5,150,105,0.65)]",
          "transition-shadow duration-200",
          "flex items-center justify-center",
        )}
        aria-label={QE_PHOTO_COPY.capture}
        style={{
          background:
            "conic-gradient(from 220deg at 50% 50%, #047857 0deg, #10b981 90deg, #34d399 180deg, #10b981 270deg, #047857 360deg)",
        }}
      >
        <span
          className="absolute inset-1 rounded-full bg-gradient-to-br from-emerald-400 via-emerald-600 to-emerald-800 shadow-inner"
          aria-hidden
        />
        <span
          className="absolute inset-x-3 top-2 h-5 rounded-full bg-white/35 blur-[2px] pointer-events-none"
          aria-hidden
        />
        <span
          className="absolute inset-x-4 bottom-1.5 h-1.5 rounded-full bg-emerald-950/40 blur-md pointer-events-none"
          aria-hidden
        />
        <Camera01
          className="relative size-9 md:size-10 text-white drop-shadow-md"
          aria-hidden
          strokeWidth={2.4}
        />
      </motion.button>
      <p className="mt-3 text-[12px] font-semibold text-zinc-700 tracking-wide">
        {QE_PHOTO_COPY.capture}
      </p>
    </div>
  )
}

/* ── AI activity chip ─────────────────────────────────────────── */

function ActivityChip({
  text,
  hasInflight,
}: {
  text: string
  hasInflight: boolean
}) {
  return (
    <div className="flex items-center justify-center">
      <motion.div
        layout
        className={cx(
          "inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full",
          "bg-indigo-50/70 border border-indigo-100",
          "text-[12px] font-semibold text-indigo-900",
        )}
      >
        <Stars02 className="size-3.5 text-indigo-600" aria-hidden />
        <span className="relative flex size-1.5">
          {hasInflight && (
            <motion.span
              className="absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-75"
              animate={{ scale: [1, 2.4, 1], opacity: [0.7, 0, 0.7] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut" }}
            />
          )}
          <span className="relative inline-flex rounded-full size-1.5 bg-indigo-500" />
        </span>
        <span className="leading-none">{text}</span>
      </motion.div>
    </div>
  )
}

/* ── Compact capture button (when photos already exist) ───────── */

function CompactCaptureButton({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      className={cx(
        "w-full h-12 rounded-2xl",
        "bg-gradient-to-br from-emerald-500 to-emerald-600",
        "hover:from-emerald-500 hover:to-emerald-700",
        "text-white shadow-md shadow-emerald-500/25 hover:shadow-emerald-600/30",
        "flex items-center justify-center gap-2 transition-shadow duration-150",
        "ring-1 ring-emerald-700/15",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300",
      )}
    >
      <Camera01 className="size-4" strokeWidth={2.5} aria-hidden />
      <span className="text-[14px] font-semibold tracking-tight">
        {QE_PHOTO_COPY.captureMore}
      </span>
    </motion.button>
  )
}

/* ── In-flight tile ───────────────────────────────────────────── */

function InflightTile({ previewUrl }: { previewUrl: string }) {
  return (
    <div className="relative aspect-square rounded-2xl overflow-hidden border border-zinc-200/80 bg-zinc-100 shadow-sm">
      <img
        src={previewUrl}
        alt={QE_PHOTO_COPY.evaluating}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <motion.div
        className="absolute inset-0 bg-zinc-900/40 backdrop-blur-[1px]"
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
        <Loader className="size-5 text-white animate-spin" aria-hidden />
        <span className="text-[10px] font-bold uppercase tracking-wide text-white drop-shadow">
          {QE_PHOTO_COPY.evaluating}
        </span>
      </div>
    </div>
  )
}
